import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Tooltip, IconButton, Divider } from "@mui/material";
import * as colors from "@mui/material/colors";
import LayersIcon from "@mui/icons-material/Layers";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PlaceIcon from "@mui/icons-material/Place";
import MapIcon from "@mui/icons-material/Map";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import AltRouteIcon from "@mui/icons-material/AltRoute";

const LAYER_OPTIONS = [
    {
        id: "standard",
        labelKey: "DASBasemap",
        thumb: "/assets/layer-thumbnails/das.png",
        layerTitle: "Standard",
    },
    {
        id: "satellite",
        labelKey: "Satellite",
        thumb: "/assets/layer-thumbnails/sat.png",
        layerTitle: "Satellite",
    },
    {
        id: "osm",
        labelKey: "OSM",
        thumb: "/assets/layer-thumbnails/osm1.png",
        layerTitle: "OSM",
    },

    {
        id: "terrain",
        labelKey: "Terrain",
        thumb: "/assets/layer-thumbnails/terr.png",
        layerTitle: "Terrain",
    },
];

const OVERLAY_OPTIONS = [
    { id: "parcel", labelKey: "Parcels", icon: < HomeWorkIcon />, layerName: "parcel" },
    { id: "building", labelKey: "AOI", icon: <LayersIcon />, layerName: "building" },
    { id: "landmarks", labelKey: "Landmarks", icon: <PlaceIcon />, layerName: "landmarks" },
    { id: "neighborhood", labelKey: "Neighborhood", icon: <LocationCityIcon />, layerName: "neighborhood" },
    { id: "roads", labelKey: "Roads", icon: <AltRouteIcon />, layerName: "roads" },
    { id: "landuse", labelKey: "LandUse", icon: <MapIcon />, layerName: "landuse" },
];

export const GoogleLayerSwitcher = ({ map }) => {
    const [activeLayer, setActiveLayer] = useState("standard");
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeOverlays, setActiveOverlays] = useState({
        parcel: true,
        building: true,
        landmarks: true,
        neighborhood: true,
        roads: true,
        landuse: true,
    });

    const { t } = useTranslation();

    useEffect(() => {
        if (!map) return;
        // Sync Base Maps (use localized BaseLayers title)
        const baseGroup = map.getLayers().getArray().find(l => l.get("title") === t('BaseLayers'));
        if (baseGroup) {
            const layers = baseGroup.getLayers().getArray();
            const visibleLayer = layers.find(l => l.getVisible());
            if (visibleLayer) {
                const option = LAYER_OPTIONS.find(o => t(o.layerTitle) === visibleLayer.get("title"));
                if (option) setActiveLayer(option.id);
            }
        }

        // Sync Overlays (use localized Overlays title)
        const featureGroup = map.getLayers().getArray().find(l => l.get("title") === t('Overlays'));
        if (featureGroup) {
            const overlays = {};
            featureGroup.getLayers().forEach(layer => {
                const name = layer.get("name");
                if (name) overlays[name] = layer.getVisible();
            });
            setActiveOverlays(prev => ({ ...prev, ...overlays }));
        }
    }, [map]);

    const handleBaseSwitch = (option) => {
        if (!map) return;

        setActiveLayer(option.id);

        // Switch base layers exclusively
        const baseGroup = map.getLayers().getArray().find(l => l.get("title") === "Base Layers");
        if (baseGroup) {
            baseGroup.getLayers().forEach((layer) => {
                const isSelected = layer.get("title") === option.layerTitle;
                layer.setVisible(isSelected);
            });
        }

        // Optional: Hide ground-obscuring overlays (like Land Use) automatically when in Satellite mode
        // if (option.id === "satellite") {
        //   handleOverlayToggle({ id: "landuse", layerName: "landuse" }, false);
        // }
    };

    const handleOverlayToggle = (option, forcedState = null) => {
        if (!map) return;
        const newState = forcedState !== null ? forcedState : !activeOverlays[option.id];
        setActiveOverlays(prev => ({ ...prev, [option.id]: newState }));

        const featureGroup = map.getLayers().getArray().find(l => l.get("title") === "Overlays");
        if (featureGroup) {
            featureGroup.getLayers().forEach(layer => {
                if (layer.get("name") === option.layerName) {
                    layer.setVisible(newState);
                }
            });
        }
    };

    return (
        <Box
            sx={{
                position: "absolute",
                bottom: 50,
                left: 24,
                zIndex: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1.5,
            }}
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
        >
            {isExpanded && (
                <Paper
                    elevation={4}
                    sx={{
                        padding: "16px",
                        borderRadius: "16px",
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                        width: "max-content",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "bold", color: colors.grey[700] }}>
                            {t('MapType')}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            {LAYER_OPTIONS.map((option) => (
                                <Box
                                    key={option.id}
                                    onClick={() => handleBaseSwitch(option)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: "10px",
                                            cursor: "pointer",
                                            backgroundImage: `url(${option.thumb})`,
                                            backgroundSize: "cover",
                                            border: `2px solid ${activeLayer === option.id ? colors.blue[500] : "transparent"}`,
                                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                                            },
                                        }}
                                    />
                                    <Typography variant="caption" sx={{
                                        fontSize: "10px",
                                        fontWeight: activeLayer === option.id ? "bold" : "normal",
                                        color: activeLayer === option.id ? colors.blue[700] : colors.grey[600]
                                    }}>
                                        {t(option.labelKey || option.label)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Divider sx={{ opacity: 0.6 }} />

                    {/* <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "bold", color: colors.grey[700] }}>
                            Map details
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
                            {OVERLAY_OPTIONS.map((option) => (
                                <Box
                                    key={option.id}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOverlayToggle(option)}
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            backgroundColor: activeOverlays[option.id] ? colors.blue[50] : colors.grey[50],
                                            color: activeOverlays[option.id] ? colors.blue[600] : colors.grey[400],
                                            border: `1px solid ${activeOverlays[option.id] ? colors.blue[200] : colors.grey[200]}`,
                                            "&:hover": {
                                                backgroundColor: activeOverlays[option.id] ? colors.blue[100] : colors.grey[100],
                                            },
                                            transition: "all 0.2s ease",
                                        }}
                                    >
                                        {option.icon}
                                    </IconButton>
                                    <Typography variant="caption" sx={{
                                        fontSize: "10px",
                                        textAlign: "center",
                                        width: "100%",
                                        color: activeOverlays[option.id] ? colors.blue[800] : colors.grey[500]
                                    }}>
                                        {option.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box> */}
                </Paper>
            )}

            {!isExpanded && (
                <Paper
                    elevation={6}
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: "12px",
                        cursor: "pointer",
                        backgroundImage: `url(${LAYER_OPTIONS.find(o => o.id === activeLayer)?.thumb})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: `3px solid white`,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            backgroundColor: "rgba(255,255,255,0.95)",
                            color: colors.grey[800],
                            textAlign: "center",
                            py: 0.6,
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                            {t('Layers')}
                        </Typography>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};
